<template>
  <v-dialog
    v-model="userPreferencesDialog"
    width="650"
    scrollable
  >
    <v-card>
      <AdCardTitle dialog>
        {{ $t("user-settings") }}
      </AdCardTitle>
      <v-card-text class="pa-7" v-if="currentUserDetails !== null">
        <v-tabs v-model="userPreferencesTab" show-arrows icons-and-text grow>
          <v-tabs-slider color="orange darken-4"></v-tabs-slider>
          <v-tab key="A">
            <div class="caption py-1">{{ $t('account') }}</div>
            <v-icon>mdi-account</v-icon>
          </v-tab>
          <v-tab key="B">
            <div class="caption py-1">{{ $t('password') }}</div>
            <v-icon>mdi-key</v-icon>
          </v-tab>
          <v-tab key="C">
            <div class="caption py-1">{{ $t('groups') }}</div>
            <v-icon>mdi-account-multiple</v-icon>
          </v-tab>
          <v-tab key="D">
            <div class="caption py-1">{{ $t('projects') }}</div>
            <v-icon>mdi-briefcase</v-icon>
          </v-tab>
          <!-- Account -->
          <v-tab-item class="pt-5">
            <v-form
              ref="userPreferencesForm"
              v-model="prefValid"
              persistent
              hide-overlay
            >
              <v-row dense>
                <v-col>
                  <v-text-field
                    v-model="currentUserDetails.name"
                    :rules="rulesUser.username"
                    :label="$t('username')"
                    required
                    readonly
                  ></v-text-field>
                  <EditableString
                   v-model.trim="currentUserDetails.displayName"
                    ref="displayName"
                    :rules="rulesUser.displayName"
                    :label="$t('display-name')"
                    required
                  />
                  <EditableEmail
                    v-model.trim="currentUserDetails.email"
                    ref="email"
                    :rules="rulesUser.email"
                    :label="$t('email')"
                    required
                  />
                  <EditableShortDescriptiveName
                    v-model.trim="currentUserDetails.organization"
                    ref="organization"
                    :rules="rulesUser.organization"
                    :label="$t('organization')"
                    :required="false"
                  />
                  <EditableDropdown
                    v-model="currentUserDetails.defaultLanguage"
                    ref="defaultLanguage"
                    :rules="rulesUser.defaultLanguage"
                    :items="$root.langs"
                    :label="$t('preferred-user-language')"
                  >
                    <template v-slot:selection="{ item }">
                      <span>{{ item.substring(0, 2) }}</span>
                      <flag
                        :squared="Boolean(0)"
                        :iso="item.substring(3, 5)"
                        style="margin-left:5px;"
                      ></flag>
                    </template>
                  </EditableDropdown>
                </v-col>
              </v-row>
            </v-form>
          </v-tab-item>
          <!-- Password -->
          <v-tab-item class="pt-5">
            <template v-if="forceLogin">
              <AdInfoOwl warning>{{ $t('confirm-with-credentials-hint') }}</AdInfoOwl>
              <div class="d-flex justify-center">
                <AdButton color="orange" :text="$t('confirm-with-credentials')" @click="$emit('needCredentials')"/>
              </div>
            </template>

            <template v-else>
              <v-form
                ref="userPreferencesPasswordForm"
                v-model="prefValid"
              >
                <!-- Support password managers when saving credentials -->
                <input hidden :value="currentUsername" type="text" autocomplete="username"/>
                <v-row class="mb-2">
                  <v-col>
                    <PasswordStrengthMeter
                      v-model="userDetailEdit.password"
                      class="password-input"
                      ref="password"
                      :label="$t('password-new')"
                      @generated="userDetailEdit.confirmPassword = $event"
                      generation
                      autocomplete="new-password"
                    />

                    <PasswordStrengthMeter
                      v-model="userDetailEdit.confirmPassword"
                      class="password-input"
                      ref="confirmPassword"
                      :match="userDetailEdit.password"
                      :label="$t('password-repeat')"
                      autocomplete="new-password"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </template>
          </v-tab-item>
          <!-- Groups -->
          <v-tab-item class="pt-7">
            <v-row dense class="pb-3">
              <v-col cols="12">
                {{ $t('you-are-member-of-groups')}}
                <span> ( + </span>
                <span class="red--text">
                  {{ $t('primarygroup') }}
                </span>
                <span>):</span>
              </v-col>
            </v-row>
            <v-row dense class="pb-5">
              <v-col>
                <span
                  v-for="(g, key) in currentUserDetails.groups"
                  :key="key"
                >
                  <v-chip
                    v-if="g === currentUserDetails.primarygroup"
                    class="red--text red lighten-5 text-truncate hidden-sm-and-down mr-2"
                  >
                    {{ g }}*
                  </v-chip>
                  <v-chip
                    v-else
                    class="orange--text orange lighten-5 text-truncate hidden-sm-and-down mr-2"
                  >
                    {{ g }}
                  </v-chip>
                </span>
              </v-col>
            </v-row>
          </v-tab-item>
          <!-- Projects -->
          <v-tab-item class="pt-5">
            <v-row dense>
              <v-col>
                <v-data-table
                  v-if="currentUserDetails.project"
                  :items="currentUserDetails.project"
                  item-key="prefix"
                  class="elevation-1"
                  :items-per-page="5"
                  :hide-default-footer="currentUserDetails.project.length <= 5"
                >
                  <template v-slot:item="p">
                    <tr>
                      <td>{{ $filters.getMatchingTranslation({value: p.item.name }) }}</td>
                      <td class="text-xs-right">{{ p.item.prefix }}</td>
                    </tr>
                  </template>
                  <template v-slot:no-data>
                    <AdInfoOwl chat>{{ $t('no-items')}}</AdInfoOwl>
                  </template>
                </v-data-table>
                <v-alert v-else :value="true" color="error" icon="mdi-alert">
                  Sorry, you are not a contributor to any project :(
                </v-alert>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs>
        <v-card-actions>
          <v-spacer></v-spacer>
          <AdButton
            type="close"
            @click="cancelUpdateUserPreferences"
          />
          <AdButton
            v-if="userPreferencesTab === 0"
            type="save"
            :text="$t('save-settings')"
            @click="updateUserPreferences"
          />
          <AdButton
            v-if="!forceLogin && userPreferencesTab === 1"
            type="save"
            :text="$t('save-password')"
            @click="updateUserPassword"/>
        </v-card-actions>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import ServerService from '@/services/ServerService'
import rulesUser from '@/rules/user'

const TOKEN_MAX_AGE = +process.env.VUE_APP_EXTRA_SECURITY_TOKEN_MAX_AGE || 1000 * 60 * 15 // 15 minutes

export default {
  name: 'UserPreferences',
  data () {
    return {
      currentUserDetails: null,
      prefValid: false,
      rulesUser,
      userPreferencesDialog: false,
      userDetailEdit: {
        confirmPassword: null,
        password: null,
        passwordOld: null
      },
      userPreferencesTab: 0,
      interval: null,
      forceLogin: true
    }
  },
  computed: {
    currentUsername () {
      return this.$store.getters['authentication/getUsername']
    },
    tokenIssued () {
      return this.$store.state.authentication.tokenIssued || null
    }
  },
  watch: {
    userPreferencesDialog: function (newValue, oldValue) {
      if (oldValue && newValue === false) {
        this.$emit('cancelUpdateUserPreferences', false)
      }
    },
    userPreferencesTab: {
      immediate: true,
      handler (tab) {
        if (tab === 1) {
          // password tab
          this.interval = setInterval(() => {
            this.forceLogin = this.tokenAgeTooOld()
          }, 1000)
        } else {
          clearInterval(this.interval)
        }
      }
    }
  },
  created () {
    this.getCurrentUserDetails()
    this.forceLogin = this.tokenAgeTooOld()
  },
  beforeDestroy () {
    clearInterval(this.interval)
  },

  methods: {
    tokenAgeTooOld () {
      const issuedAt = new Date(this.tokenIssued).getTime()
      const now = Date.now()
      return now - issuedAt > TOKEN_MAX_AGE
    },
    async getCurrentUserDetails () {
      try {
        const response = (await ServerService.getUser(this.currentUsername))
          .data
        this.currentUserDetails = response
        if (!Object.prototype.hasOwnProperty.call(this.currentUserDetails, 'defaultLanguage')) {
          this.$set(this.currentUserDetails, 'defaultLanguage', 'en-US')
        }
        if (!Object.prototype.hasOwnProperty.call(this.userDetailEdit, 'password')) {
          this.$set(this.userDetailEdit, 'password', null)
        }
        if (!Object.prototype.hasOwnProperty.call(this.userDetailEdit, 'passwordOld')) {
          this.$set(this.userDetailEdit, 'passwordOld', null)
        }
        if (!Object.prototype.hasOwnProperty.call(this.userDetailEdit, 'confirmPassword')) {
          this.$set(this.userDetailEdit, 'confirmPassword', null)
        }
        this.$nextTick(() => {
          this.userPreferencesDialog = true
        })
      } catch (error) {
        this.$message({
          message: error,
          duration: 0,
          showClose: true,
          type: 'error'
        })
      }
    },
    resetUserPreferences () {
      this.userPreferencesTab = 0
      this.currentUserDetails = null
      this.userDetailEdit.confirmPassword = null
      this.userDetailEdit.password = null
      this.userDetailEdit.passwordOld = null
    },
    async updateUserPreferences () {
      if (this.$refs.userPreferencesForm.validate()) {
        const updatedUser = await ServerService.updateUser(
          this.currentUserDetails.name,
          this.currentUserDetails
        ).catch(error => {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || error?.message || 'Error'
          this.$oops(errorMessage)
          return null
        })
        if (!updatedUser) return
        if (updatedUser?.data !== null) {
          this.$notify(this.$t('submission-save-success-label'), 'success')

          // If preferred language is updated, change current locale
          if (this.$store.getters['authentication/getUserDefaultLanguage'] !== updatedUser.data.defaultLanguage) {
            this.$i18n.setLocale(updatedUser.data.defaultLanguage)
            if (this.projectLocales?.length > 1 && this.projectLocales.includes(updatedUser.data.defaultLanguage)) {
              this.$store.commit('app/setProjectLanguage', updatedUser.data.defaultLanguage)
            }
            this.$store.commit('authentication/setUserDefaultLanguage', updatedUser.data.defaultLanguage)
          }
        }
        // Close dialog
        this.cancelUpdateUserPreferences()
      } else {
        const hasError = rulesUser.requiredAttributes.filter(attr => this.$refs[attr] && this.$refs[attr].hasError)
        if (hasError && hasError[0] && this.$refs[hasError[0]]) {
          this.$refs[hasError[0]].focus()
        }
      }
    },
    async updateUserPassword () {
      if (!this.$refs.userPreferencesPasswordForm.validate()) {
        this.$oops(this.$t('passwords-invalid'))
        return
      }

      if (this.currentUserDetails.name) {
        const { password, confirmPassword } = this.userDetailEdit

        try {
          await ServerService.updateUserPassword({
            username: this.currentUserDetails.name,
            password,
            confirmPassword
          })
          this.$notify(this.$t('submission-save-success-label'), 'success')

          // Close dialog
          this.cancelUpdateUserPreferences()
        } catch (error) {
          const errorMessage = error?.response?.data?.description || error?.response?.statusText || 'Error'
          this.$oops(errorMessage)
        }
      }
    },
    cancelUpdateUserPreferences () {
      this.userPreferencesDialog = false
      this.$nextTick(() => {
        // reset to first tab
        this.resetUserPreferences()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep .v-input__control {
  max-width: 400px;
}
</style>
